(() => {
  'use strict';

  const CONFIG_SELECTOR = 'fk-folienschnitt-customizer';
  const INPUT_SELECTOR = '[data-fk-cut-text]';
  const COUNTER_SELECTOR = '[data-fk-cut-count]';
  const OVERLAY_CLASS = 'fk-cut-image-preview';
  const OVERLAY_TEXT_CLASS = 'fk-cut-image-preview__text';

  function getConfigFromElement(element) {
    return element && element.closest ? element.closest(CONFIG_SELECTOR) : null;
  }

  function getSection(config) {
    return config && config.closest ? (config.closest('.shopify-section') || document) : document;
  }

  function getInput(config) {
    return config ? config.querySelector(INPUT_SELECTOR) : null;
  }

  function getCounter(config) {
    return config ? config.querySelector(COUNTER_SELECTOR) : null;
  }

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLocaleUpperCase(document.documentElement.lang || 'de-DE');
  }

  function getSelectedVariantText(config) {
    const root = getSection(config);
    const values = [];

    root.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      let label = null;
      if (input.id) {
        try {
          label = root.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        } catch (error) {
          label = null;
        }
      }
      const value = String((label && label.textContent) || input.value || '').trim().replace(/\s+/g, ' ');
      if (value && !values.includes(value)) values.push(value);
    });

    root.querySelectorAll('select').forEach((select) => {
      const option = select.options && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
      const value = String((option && option.textContent) || '').trim().replace(/\s+/g, ' ');
      if (value && !values.includes(value)) values.push(value);
    });

    return values.join(' ');
  }

  function getTextColor(config) {
    const value = getSelectedVariantText(config).toLowerCase();
    if (value.includes('weiß') || value.includes('weiss')) return '#ffffff';
    if (value.includes('rot')) return '#c91f28';
    if (value.includes('gelb')) return '#f1cf00';
    if (value.includes('blau')) return '#155a9a';
    return '#111111';
  }

  function getFontSettings(config) {
    const value = getSelectedVariantText(config).toLowerCase();

    if (value.includes('sc03') || value.includes('engschrift')) {
      return { family: "'Arial Narrow', Arial, sans-serif", weight: '800', style: 'normal', spacing: '.005em' };
    }

    if (value.includes('sc02') || value.includes('kursiv')) {
      return { family: 'Arial, Helvetica, sans-serif', weight: '800', style: 'italic', spacing: '.01em' };
    }

    return { family: 'Arial, Helvetica, sans-serif', weight: '800', style: 'normal', spacing: '.01em' };
  }

  function findImageHost(config) {
    const root = getSection(config);
    const selectors = [
      'media-gallery slideshow-slide[aria-hidden="false"] .product-media',
      'media-gallery slideshow-slide .product-media',
      'media-gallery .product-media',
      '[data-fk-product-media-simple] .fk-product-media-simple__item:first-child',
      '.product-media'
    ];

    for (const selector of selectors) {
      const elements = Array.from(root.querySelectorAll(selector));
      const host = elements.find((element) => !element.closest('zoom-dialog, .dialog-zoomed-gallery'));
      if (host) return host;
    }

    return null;
  }

  function ensureOverlay(config) {
    const host = findImageHost(config);
    if (!host) return null;

    host.classList.add('fk-cut-image-host');

    let overlay = host.querySelector(':scope > .' + OVERLAY_CLASS);
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = OVERLAY_CLASS;
      overlay.setAttribute('aria-hidden', 'true');

      const text = document.createElement('span');
      text.className = OVERLAY_TEXT_CLASS;
      overlay.appendChild(text);
      host.appendChild(overlay);
    }

    return overlay;
  }

  function getProductForm(config) {
    const root = getSection(config);
    return root.querySelector('form[data-type="add-to-cart-form"]') || root.querySelector('product-form-component form') || null;
  }

  function ensureHiddenProperty(form, key) {
    let input = form.querySelector('[data-fk-cut-property="' + key + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + key + ']';
      input.setAttribute('data-fk-cut-property', key);
      form.appendChild(input);
    }
    return input;
  }

  function syncForm(config) {
    const form = getProductForm(config);
    if (!form) return;

    const input = getInput(config);
    const text = normalizeText(input ? input.value : '');
    const prefix = config.dataset.prefix || '';

    ensureHiddenProperty(form, 'Wunschtext').value = text;
    ensureHiddenProperty(form, '_Folienschnitt-Vorschau').value = text ? prefix + text : '';
    ensureHiddenProperty(form, '_Konfigurator').value = 'Fireandkisses Folienschnitt | Version 5';
    ensureHiddenProperty(form, '_Produkt-ID').value = config.dataset.productId || '';
  }

  function fitOverlay(overlay) {
    if (!overlay) return;
    const text = overlay.querySelector('.' + OVERLAY_TEXT_CLASS);
    if (!text || overlay.clientWidth < 1 || overlay.clientHeight < 1) return;

    let size = Math.max(12, overlay.clientHeight * 0.56);
    text.style.fontSize = size + 'px';

    const maxWidth = overlay.clientWidth * 0.94;
    if (text.scrollWidth > maxWidth && text.scrollWidth > 0) {
      size = size * (maxWidth / text.scrollWidth);
    }

    text.style.fontSize = Math.max(10, size) + 'px';
  }

  function updateConfig(config) {
    if (!config) return;

    const input = getInput(config);
    if (!input) return;

    const counter = getCounter(config);
    if (counter) counter.textContent = String(Array.from(input.value || '').length);

    const overlay = ensureOverlay(config);
    if (overlay) {
      const textElement = overlay.querySelector('.' + OVERLAY_TEXT_CLASS);
      const text = normalizeText(input.value);
      const prefix = config.dataset.prefix || '';
      const finalText = text ? prefix + text : '';
      const color = getTextColor(config);
      const font = getFontSettings(config);

      if (textElement) {
        textElement.textContent = finalText;
        textElement.style.color = color;
        textElement.style.fontFamily = font.family;
        textElement.style.fontWeight = font.weight;
        textElement.style.fontStyle = font.style;
        textElement.style.letterSpacing = font.spacing;
      }

      overlay.classList.toggle('is-visible', Boolean(finalText));
      overlay.classList.toggle('is-white', color === '#ffffff');
      window.requestAnimationFrame(() => fitOverlay(overlay));
    }

    syncForm(config);
  }

  function updateAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(CONFIG_SELECTOR).forEach(updateConfig);
    if (scope.matches && scope.matches(CONFIG_SELECTOR)) updateConfig(scope);
  }

  function validateBeforeSubmit(form, event) {
    const section = form.closest('.shopify-section') || document;
    const config = section.querySelector(CONFIG_SELECTOR);
    if (!config) return;

    const input = getInput(config);
    const text = normalizeText(input ? input.value : '');

    if (!text) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (input) {
        input.setCustomValidity('Bitte gib den gewünschten Text ein.');
        input.reportValidity();
        input.focus();
      }
      return;
    }

    if (input) input.setCustomValidity('');
    syncForm(config);
  }

  function start() {
    updateAll(document);

    document.addEventListener('input', (event) => {
      if (!event.target || !event.target.matches || !event.target.matches(INPUT_SELECTOR)) return;
      const config = getConfigFromElement(event.target);
      if (event.target.setCustomValidity) event.target.setCustomValidity('');
      updateConfig(config);
    });

    document.addEventListener('change', (event) => {
      const section = event.target && event.target.closest ? event.target.closest('.shopify-section') : null;
      if (!section) return;
      const config = section.querySelector(CONFIG_SELECTOR);
      if (!config) return;
      updateConfig(config);
      window.setTimeout(() => updateConfig(config), 120);
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      validateBeforeSubmit(form, event);
    }, true);

    document.addEventListener('shopify:section:load', (event) => {
      updateAll(event.target);
    });

    window.addEventListener('resize', () => updateAll(document));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();